import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Loader2, Store, TrendingUp, TrendingDown } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend } from "recharts";
import api from "../../lib/api";

export function MultiStoreTab({ startDate, endDate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.request(`/analytics/multi-store?${new URLSearchParams({ ...(startDate && { start_date: startDate }), ...(endDate && { end_date: endDate }) })}`)
      .then(setData).catch(console.error).finally(() => setLoading(false));
  }, [startDate, endDate]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  if (!data || data.comparison.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-20 text-center">
        <Store className="w-10 h-10 text-muted-foreground/30" />
        <p className="text-sm text-muted-foreground">No hay múltiples restaurantes para comparar.</p>
        <p className="text-xs text-muted-foreground">Esta vista se activa cuando tienes más de un restaurante en tu cuenta.</p>
      </div>
    );
  }

  const maxRev = Math.max(...data.comparison.map(r => r.total), 1);
  const COLORS = ["hsl(var(--primary))", "#6366f1", "#0ea5e9", "#22c55e", "#f59e0b"];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Badge variant="outline" className="text-xs">{data.comparison.length} restaurantes</Badge>
        <Badge variant="outline" className="text-xs">{data.period.start} – {data.period.end}</Badge>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.comparison.map((r, i) => (
          <Card key={r.restaurant_id} data-testid={`multistore-card-${r.restaurant_id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{r.name}</p>
                  <p className="text-2xl font-bold mt-1">{r.total.toFixed(0)}€</p>
                  <div className="flex gap-3 mt-1 text-xs text-muted-foreground">
                    <span>{r.transactions} tx</span>
                    <span>·</span>
                    <span>tíc. {r.avg_ticket.toFixed(2)}€</span>
                  </div>
                </div>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${COLORS[i % COLORS.length]}20` }}>
                  <Store className="w-4 h-4" style={{ color: COLORS[i % COLORS.length] }} />
                </div>
              </div>
              <div className="mt-3 h-1.5 rounded-full bg-muted overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${(r.total / maxRev) * 100}%`, backgroundColor: COLORS[i % COLORS.length] }} />
              </div>
              <p className="text-xs text-muted-foreground mt-1">Beneficio neto: {r.net_profit.toFixed(0)}€</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Comparison bar chart */}
      <Card data-testid="multistore-chart">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Comparativa de ventas</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.comparison} barSize={40}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
              <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickFormatter={v => `${v}€`} />
              <Tooltip formatter={v => [`${Number(v).toFixed(2)}€`]} />
              <Bar dataKey="total" name="Ventas" radius={[6, 6, 0, 0]}>
                {data.comparison.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
              <Bar dataKey="net_profit" name="Beneficio neto" radius={[6, 6, 0, 0]}>
                {data.comparison.map((_, i) => <Cell key={i} fill={`${COLORS[i % COLORS.length]}80`} />)}
              </Bar>
              <Legend iconType="square" wrapperStyle={{ fontSize: 11 }} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* KPI Table */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Tabla comparativa de KPIs</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border">
                <tr className="text-xs text-muted-foreground uppercase">
                  <th className="text-left px-4 py-2">Restaurante</th>
                  <th className="text-right px-4 py-2">Ventas</th>
                  <th className="text-right px-4 py-2">Transacciones</th>
                  <th className="text-right px-4 py-2">Tíc. medio</th>
                  <th className="text-right px-4 py-2">Beneficio neto</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.comparison.map((r, i) => (
                  <tr key={r.restaurant_id} className="hover:bg-muted/30">
                    <td className="px-4 py-2.5 font-medium">{r.name}</td>
                    <td className="px-4 py-2.5 text-right font-bold">{r.total.toFixed(2)}€</td>
                    <td className="px-4 py-2.5 text-right">{r.transactions}</td>
                    <td className="px-4 py-2.5 text-right">{r.avg_ticket.toFixed(2)}€</td>
                    <td className="px-4 py-2.5 text-right">{r.net_profit.toFixed(2)}€</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
